/**
 * PcoFundingSection — read-only Funding breakdown atop the PCO Detail Totals
 * tab (funding buckets, findings #13 + #8; lived on the Overview tab until
 * that tab was retired 2026-08-05).
 *
 * Lists the parent PCO's funding buckets (funding_tranches, 0..N) with their
 * derived Pending / Funded status. On screen the funded state SAYS "Funded"
 * (client 2026-08-02, extending the PCO-062 "Funded not Approved" ruling —
 * approved-the-money kept colliding with approved-the-bill); the enum, the
 * s-approved class, and the xlsx export's own "Approved" language are all
 * unchanged. Editing moved into "Edit details" — this card is now a breakdown
 * with a link across to that modal.
 *
 * A bucket's status is STORED — `funding_tranches.is_funded` (client ruling
 * 2026-08-06), read through `trancheIsFunded` so this card and the Edit modal
 * can't drift and so an API predating the column still classifies correctly.
 * It used to be derived from whether the row carried a MOD / FWA / date, which
 * is why a Pending row here can now show references: the GC issues them before
 * the funding executes. When the PCO still carries a legacy proposed amount
 * above its funded sum, the residual shows as a trailing Pending row so the
 * breakdown reconciles with the stat strip's Pending figure.
 */
import { SquarePen } from 'lucide-react';
import { formatMoneyCents } from '../lib/format';
import { trancheIsFunded } from '../lib/pcoMoney';
import type { FundingTranche } from '@coffman-tm/shared';

interface Props {
  tranches: FundingTranche[];
  loading: boolean;
  /** Legacy proposed amount (null once retired) — drives the residual row. */
  amountProposedCents: number | null;
  /** Opens the Edit Details modal, where buckets are actually edited. */
  onEditDetails: () => void;
}

export function PcoFundingSection({ tranches, loading, amountProposedCents, onEditDetails }: Props) {
  if (loading && tranches.length === 0) return null;

  const approvedSum = tranches
    .filter(trancheIsFunded)
    .reduce((a, t) => a + t.approved_amount_cents, 0);
  const residualCents = Math.max(0, (amountProposedCents ?? 0) - approvedSum);
  const bucketCount = tranches.length + (residualCents > 0 ? 1 : 0);
  // Imported history rarely carried funded dates — an all-dash column is
  // furniture, so it renders only once at least one bucket has a real date.
  const anyFundedDate = tranches.some((t) => t.approved_date !== null);

  return (
    <div className="section section--card">
      <div className="section-head">
        <div className="section-head-left">
          <h2 className="section-title">Funding</h2>
          {approvedSum > 0 && (
            <span className="muted" style={{ fontSize: 12.5 }}>
              {formatMoneyCents(approvedSum)} · {bucketCount} bucket
              {bucketCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
        {/* Plain "Edit details" — the label used to name the destination in
            quotes ('Edit in "Edit details"'), which read as a riddle. The
            arrow went with it: this opens a modal in place, it doesn't
            navigate. Pencil + bold accent (Brandon 2026-08-05): as quiet gray
            text it didn't read as an action at all. */}
        <button
          type="button"
          className="btn-link btn-link-strong"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5 }}
          onClick={onEditDetails}
        >
          <SquarePen size={15} aria-hidden />
          Edit details
        </button>
      </div>

      {tranches.length === 0 && residualCents === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>
          No buckets yet. PCO is in proposed-only state.{' '}
          <button type="button" className="btn-link" onClick={onEditDetails}>
            Add one in Edit details
          </button>
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Status</th>
              <th>MOD #</th>
              {/* Plain "FWA #" — the GC-name prefix was furniture on a
                  single-GC project (client, 2026-08-02). The GC's name still
                  appears wherever prose needs it, always via gcName/gcShort. */}
              <th>FWA #</th>
              {anyFundedDate && <th>Funded date</th>}
              <th className="num">Amount</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            {tranches.map((t) => {
              const approved = trancheIsFunded(t);
              return (
                <tr key={t.id}>
                  <td>
                    <span className={`status-chip ${approved ? 's-approved' : 's-pending'}`}>
                      {approved ? 'Funded' : 'Pending'}
                    </span>
                  </td>
                  <td className="mono" style={{ fontSize: 13 }}>{t.mod_number ?? '—'}</td>
                  <td className="mono" style={{ fontSize: 13 }}>{t.hoffman_fwa ?? '—'}</td>
                  {anyFundedDate && (
                    <td className="muted" style={{ fontSize: 13 }}>{t.approved_date ?? '—'}</td>
                  )}
                  <td className="num">{formatMoneyCents(t.approved_amount_cents)}</td>
                  <td className="muted" style={{ fontSize: 13 }}>{t.comments ?? ''}</td>
                </tr>
              );
            })}
            {residualCents > 0 && (
              <tr>
                <td>
                  <span className="status-chip s-pending">Pending</span>
                </td>
                <td className="muted" style={{ fontSize: 13 }}>—</td>
                <td className="muted" style={{ fontSize: 13 }}>—</td>
                {anyFundedDate && <td className="muted" style={{ fontSize: 13 }}>—</td>}
                <td className="num">{formatMoneyCents(residualCents)}</td>
                <td className="muted" style={{ fontSize: 12.5 }}>Proposed, not yet bucketed</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
